/**
 * Layout-shaped loading placeholders.
 *
 * A spinner or a "Loading…" block collapses the page into a single card.
 * These keep the same geometry the loaded screen will occupy, so the
 * content does not jump when the request returns.
 */
import type { CSSProperties } from "react";

function Bone({
  w = "100%", h = 12, r = 8, style,
}: { w?: number | string; h?: number; r?: number; style?: CSSProperties }) {
  return (
    <span
      className="cm-skel"
      style={{ width: w, height: h, borderRadius: r, ...style }}
    />
  );
}

export function CampaignCardSkeleton() {
  return (
    <div
      aria-hidden="true"
      style={{
        aspectRatio: "3 / 4",
        borderRadius: 16,
        overflow: "hidden",
        border: "1px solid var(--line)",
        background: "var(--surface)",
        display: "grid",
        alignContent: "end",
        padding: 16,
        gap: 10,
      }}
    >
      <Bone w="42%" h={18} r={99} />
      <Bone w="78%" h={16} />
      <Bone w="56%" h={12} />
    </div>
  );
}

export function CampaignGridSkeleton({ count = 8 }: { count?: number }) {
  return (
    <div
      className="cm-campaign-grid"
      role="status"
      aria-label="Loading campaigns"
      style={{
        display: "grid",
        gap: "clamp(10px, 1.2vw, 16px)",
        gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
      }}
    >
      {Array.from({ length: count }, (_, i) => <CampaignCardSkeleton key={i} />)}
    </div>
  );
}

export function RibbonSkeleton({ count = 4 }: { count?: number }) {
  return (
    <div role="status" aria-label="Loading" style={{ display: "grid", gap: 10 }}>
      {Array.from({ length: count }, (_, i) => (
        <div
          key={i}
          className="cm-work-ribbon"
          style={{
            display: "grid",
            gridTemplateColumns: "54px minmax(160px, 1.2fr) minmax(280px, 3fr) auto",
            alignItems: "center",
            gap: 16,
            padding: "12px 16px", border: "1px solid var(--line)",
            borderRadius: 14, background: "var(--surface)",
          }}
        >
          <Bone w={54} h={72} r={10} />
          <div style={{ display: "grid", gap: 8 }}>
            <Bone w="70%" h={14} />
            <Bone w="40%" h={10} />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 12 }}>
            <Bone w="80%" h={28} />
            <Bone w="80%" h={28} />
            <Bone w="80%" h={28} />
            <Bone w="80%" h={28} />
          </div>
          <Bone w={72} h={32} r={8} />
        </div>
      ))}
    </div>
  );
}

export function CardListSkeleton({ count = 3 }: { count?: number }) {
  return (
    <div role="status" aria-label="Loading" style={{ display: "grid", gap: 12 }}>
      {Array.from({ length: count }, (_, i) => (
        <div
          key={i}
          style={{
            padding: 20, border: "1px solid var(--line)",
            borderRadius: 12, background: "var(--surface)", display: "grid", gap: 12,
          }}
        >
          <Bone w="28%" h={10} />
          <Bone w="62%" h={16} />
          <Bone w="90%" h={12} />
          <Bone w="74%" h={12} />
        </div>
      ))}
    </div>
  );
}

export function FormSkeleton() {
  return (
    <div role="status" aria-label="Loading" style={{ display: "grid", gap: 16, maxWidth: 620 }}>
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          style={{
            padding: 20, border: "1px solid var(--line)",
            borderRadius: 12, background: "var(--surface)", display: "grid", gap: 14,
          }}
        >
          <Bone w="22%" h={10} />
          <Bone w="100%" h={36} r={6} />
          <Bone w="100%" h={36} r={6} />
        </div>
      ))}
    </div>
  );
}

export function PageSkeleton() {
  return (
    <div role="status" aria-label="Loading" style={{ display: "grid", gap: 20 }}>
      <div style={{ display: "grid", gap: 8, paddingBottom: 20, borderBottom: "1px solid var(--line)" }}>
        <Bone w={80} h={10} />
        <Bone w="36%" h={28} />
        <Bone w="52%" h={14} />
      </div>
      <CardListSkeleton count={3} />
    </div>
  );
}

export function TileRowSkeleton({ count = 3 }: { count?: number }) {
  return (
    <div role="status" aria-label="Loading" style={{
      display: "grid", gridTemplateColumns: `repeat(${count}, minmax(0, 1fr))`, gap: 16,
    }}>
      {Array.from({ length: count }, (_, i) => (
        <div key={i} style={{
          background: "var(--surface)", border: "1px solid var(--line)",
          borderRadius: 16, padding: 20, display: "grid", gap: 12,
        }}>
          <Bone w="48%" h={10} />
          <Bone w="36%" h={28} />
        </div>
      ))}
    </div>
  );
}

export { Bone as SkeletonBone };
